import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { switchMap } from 'rxjs';

import { AppEvent, EventRound } from './models';
import { EventService } from './event.service';
import { UserService } from '../global/userService/user-service';

interface ManageCard {
    key: string;
    route: string;
    icon: string;
    titleKey: string;
    noteKey?: string;
    style: 'prominent' | 'secondary' | 'neutral';
}

/**
 * The organiser hub an event's Manage Event button leads to. Consolidates what were three
 * separate buttons on event-detail.page.ts (Edit Event, Manage Registrations, Manage Rounds)
 * into one entry point, ordered by where the event actually is in its lifecycle rather than
 * a fixed order -- see the design at
 * docs/superpowers/specs/2026-09-30-organiser-event-hub-design.md.
 */
@Component({
    standalone: false,
    selector: 'abs-event-manage',
    template: `
        <abs-page-layout [pageTitle]="'GLOBAL.EVENTS.MANAGE.TITLE' | translate">
            @if (event(); as event) {
                @if (isOrganiser()) {
                    <h2 class="manage-event-title">{{ event.title }}</h2>
                    <div class="manage-cards">
                        @for (card of cards(); track card.key) {
                            <ion-card class="manage-card" [class]="'manage-card--' + card.style" [routerLink]="[card.route]">
                                <ion-card-header>
                                    <ion-icon [name]="card.icon"></ion-icon>
                                    <ion-card-title>{{ card.titleKey | translate }}</ion-card-title>
                                </ion-card-header>
                                @if (card.style === 'secondary' && card.noteKey) {
                                    <ion-card-content class="manage-card-note">
                                        {{ card.noteKey | translate }}
                                    </ion-card-content>
                                }
                            </ion-card>
                        }
                    </div>
                } @else {
                    <p class="manage-not-authorized">{{ 'GLOBAL.EVENTS.MANAGE.NOT_AUTHORIZED' | translate }}</p>
                }
            }
        </abs-page-layout>
    `
})
export class EventManagePage implements OnInit {
    private eventService = inject(EventService);
    private userService = inject(UserService);
    private route = inject(ActivatedRoute);

    event = signal<AppEvent | null>(null);
    rounds = signal<EventRound[]>([]);

    private currentUserId = computed(() => this.userService.user()?.userId);

    readonly isOrganiser = computed(() => {
        const event = this.event();
        const userId = this.currentUserId();
        if (!event || !userId) return false;
        return event.organiserId === userId || (event.coOrganisers ?? []).includes(userId);
    });

    // "Started" is judged by a round actually having started, not the event's start date,
    // which can pass while the organiser is still setting up -- matches the precedent in
    // EventService.withdrawList (api/src/event/event.service.ts).
    readonly eventStarted = computed(() => this.rounds().some((r) => r.status !== 'pending'));

    readonly cards = computed<ManageCard[]>(() => {
        const started = this.eventStarted();
        const registrationsCard: ManageCard = {
            key: 'registrations',
            route: './registrations',
            icon: 'people-outline',
            titleKey: 'GLOBAL.EVENTS.MANAGE.REGISTRATIONS_CARD',
            noteKey: 'GLOBAL.EVENTS.MANAGE.REGISTRATIONS_STILL_AVAILABLE',
            style: started ? 'secondary' : 'prominent'
        };
        const roundsCard: ManageCard = {
            key: 'rounds',
            route: './rounds',
            icon: 'trophy-outline',
            titleKey: 'GLOBAL.EVENTS.MANAGE.ROUNDS_CARD',
            noteKey: 'GLOBAL.EVENTS.MANAGE.ROUNDS_STILL_AVAILABLE',
            style: started ? 'prominent' : 'secondary'
        };
        const editCard: ManageCard = {
            key: 'edit',
            route: './edit',
            icon: 'create-outline',
            titleKey: 'GLOBAL.EVENTS.MANAGE.EDIT_CARD',
            style: 'neutral'
        };
        return started ? [roundsCard, registrationsCard, editCard] : [registrationsCard, roundsCard, editCard];
    });

    ngOnInit() {
        this.route.paramMap
            .pipe(switchMap((params) => this.eventService.getEvent(params.get('eventId')!)))
            .subscribe((event) => this.event.set(event));

        this.route.paramMap
            .pipe(switchMap((params) => this.eventService.getRounds(params.get('eventId')!)))
            .subscribe((rounds) => this.rounds.set(rounds));
    }
}
